import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Notice, OnOffBadge, Switch } from '@/admin/AdminShell'
import { fetchTaskExtractionSwitch, saveTaskExtractionSwitch } from '@/ai/taskProposalsApi'
import { ApiError } from '@/lib/api'

/**
 * Выключатель «ИИ предлагает задачи из писем, звонков и комментариев» (тикет #131).
 *
 * Засеян выключенным: включённый, он отправляет модели каждое сопоставленное письмо,
 * расшифрованный звонок и комментарий, то есть тратит токены, — и это решение о деньгах,
 * которое не принимается установкой релиза.
 */
export function TaskExtractionPanel() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const mayEdit = can(user, 'system.integration', 'update')
  const state = useQuery({ queryKey: ['admin-ai-tasks'], queryFn: ({ signal }) => fetchTaskExtractionSwitch(signal) })
  const [notice, setNotice] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (enabled: boolean) => saveTaskExtractionSwitch(enabled),
    onSuccess: (next) => {
      queryClient.setQueryData(['admin-ai-tasks'], next)
      setNotice(null)
    },
    onError: (failure: unknown) => setNotice(failure instanceof ApiError ? failure.message : t('app.error')),
  })
  if (state.isError) {
    return <Notice text={state.error instanceof ApiError ? state.error.message : t('app.error')} />
  }
  if (!state.data) return null
  return (
    <section className="panel stack" style={{ gap: 12 }} aria-label={t('aiTasks.title')}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{t('aiTasks.title')}</span>
        <OnOffBadge on={state.data.enabled} />
      </div>
      <p className="t-sm muted" style={{ margin: 0 }}>{t('aiTasks.intro')}</p>
      <Switch
        checked={state.data.enabled}
        disabled={!mayEdit || save.isPending}
        label={t('aiTasks.enabled')}
        onChange={(value) => save.mutate(value)}
      />
      <Notice text={notice} />
    </section>
  )
}
